import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../context/authContext';
import {
  changeApplicationStatus,
  createApplication,
  createQualification,
  deleteQualification,
  listApplications,
  listQualifications,
  updateApplication,
} from '../../api/academic';
import { listLookup } from '../../api/masterData';
import { ApplicationStatus } from '../../types/academic';
import { ApplicationDetailsDialog } from './ApplicationDetailsDialog';
import { Application } from '../../types/academic';
import { RequestTransferDialog } from '../intake-transfers/RequestTransferDialog';
import { StudentTransferRequests } from '../intake-transfers/StudentTransferRequests';
import { NON_TRANSFERABLE_STATUSES } from '../intake-transfers/transferUtils';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';

const APPLICATION_STATUSES: ApplicationStatus[] = [
  'APPLIED',
  'OFFER_RECEIVED',
  'OFFER_ACCEPTED',
  'CONDITIONAL_OFFER',
  'UNCONDITIONAL_OFFER',
  'COE_ISSUED',
  'VISA_LODGED',
  'VISA_APPROVED',
  'VISA_REJECTED',
  'READY_TO_FLY',
  'DEPARTED',
  'ARRIVED',
  'DEFERRED',
  'WITHDRAWN',
  'REJECTED',
];

export const StudentAcademicTab = ({
  studentId,
  highlightTransferId,
}: {
  studentId: number;
  /** From a transfer notification link (`?transfer=<id>`). */
  highlightTransferId?: number | null;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canApprove = hasPermission('academic', 'approve');
  const canRequestTransfer = hasPermission('students', 'update');
  const [transferApplication, setTransferApplication] = useState<Application | null>(null);
  const [qualOpen, setQualOpen] = useState(false);
  const [qualName, setQualName] = useState('');
  const [qualInstitution, setQualInstitution] = useState('');
  const [qualYear, setQualYear] = useState('');

  const [appOpen, setAppOpen] = useState(false);
  const [countryId, setCountryId] = useState<number | ''>('');
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [facultyId, setFacultyId] = useState<number | ''>('');
  const [courseId, setCourseId] = useState<number | ''>('');
  const [detailsApplicationId, setDetailsApplicationId] = useState<number | null>(null);

  const qualificationsQuery = useQuery({
    queryKey: ['qualifications', studentId],
    queryFn: () => listQualifications(studentId),
  });
  const applicationsQuery = useQuery({
    queryKey: ['applications', studentId],
    queryFn: () => listApplications({ page: 1, pageSize: 50, studentId }),
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
    enabled: appOpen,
  });
  const universitiesQuery = useQuery({
    queryKey: ['universities-for-app', countryId],
    queryFn: () => listLookup('/master-data/universities', { countryId, activeOnly: true }),
    enabled: appOpen && countryId !== '',
  });
  const facultiesQuery = useQuery({
    queryKey: ['faculties-for-app', universityId],
    queryFn: () => listLookup('/master-data/faculties', { universityId, activeOnly: true }),
    enabled: appOpen && universityId !== '',
  });
  // Faculty/Program hierarchy correction: Course ("Program") now depends
  // on the selected Faculty, not the University directly.
  const coursesQuery = useQuery({
    queryKey: ['courses-for-app', facultyId],
    queryFn: () => listLookup('/master-data/courses', { facultyId, activeOnly: true }),
    enabled: appOpen && facultyId !== '',
  });
  const visaStatusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/visa-statuses'],
    queryFn: () => listLookup('/master-data/visa-statuses'),
  });

  const addQualification = useMutation({
    mutationFn: () =>
      createQualification(studentId, {
        name: qualName,
        institution: qualInstitution,
        yearCompleted: qualYear ? Number(qualYear) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualifications', studentId] });
      setQualOpen(false);
      setQualName('');
      setQualInstitution('');
      setQualYear('');
    },
  });

  const deleteQualificationMutation = useMutation({
    mutationFn: (id: number) => deleteQualification(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qualifications', studentId] }),
  });

  const addApplication = useMutation({
    mutationFn: () => createApplication({ studentId, universityId: universityId as number, courseId: courseId as number }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications', studentId] });
      setAppOpen(false);
      setCountryId('');
      setUniversityId('');
      setFacultyId('');
      setCourseId('');
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ApplicationStatus }) => changeApplicationStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['applications', studentId] }),
  });

  const setVisaStatus = useMutation({
    mutationFn: ({ id, visaStatusId }: { id: number; visaStatusId: number }) =>
      updateApplication(id, { visaStatusId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['applications', studentId] }),
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Qualifications</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setQualOpen(true)}>
          Add
        </Button>
      </Stack>
      <Stack spacing={1} sx={{ mb: 3 }}>
        {qualificationsQuery.data?.map((q) => (
          <Paper key={q.id} variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2">
                {q.name} — {q.institution} {q.yearCompleted ? `(${q.yearCompleted})` : ''}
              </Typography>
            </Box>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => deleteQualificationMutation.mutate(q.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        ))}
        {qualificationsQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No qualifications recorded.
          </Typography>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Applications</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAppOpen(true)}>
          New application
        </Button>
      </Stack>
      <Stack spacing={1.5}>
        {applicationsQuery.data?.data.map((a) => (
          <Paper key={a.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle2">
                  {a.applicationNumber} — {a.university.name} / {a.course.name}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={a.status.replace(/_/g, ' ')} />
                  <Chip size="small" variant="outlined" label={`Intake: ${a.intake?.name ?? 'Not set'}`} />
                  {a.visaStatus && <Chip size="small" label={`Visa: ${a.visaStatus.name}`} />}
                  {a.coeNumber && <Chip size="small" label={`COE: ${a.coeNumber}`} />}
                  {a.assignedTo && (
                    <Chip size="small" variant="outlined" label={`${a.assignedTo.firstName} ${a.assignedTo.lastName}`} />
                  )}
                  {/* Admission Officer workflow (§3.6/§15/§17): the
                      existing Batch/Badge assignment, if any — never
                      auto-assigned, so "Not assigned" until an Admission
                      Officer explicitly assigns one from Ready to Fly. */}
                  <Chip
                    size="small"
                    color={a.travelBatchMember ? 'info' : 'default'}
                    variant={a.travelBatchMember ? 'filled' : 'outlined'}
                    onClick={
                      a.travelBatchMember
                        ? () => navigate(`/ready-to-fly?tab=batches&batch=${a.travelBatchMember!.travelBatch.id}`)
                        : undefined
                    }
                    label={
                      a.travelBatchMember
                        ? `Batch: ${a.travelBatchMember.travelBatch.batchNumber}`
                        : 'Batch: Not assigned'
                    }
                  />
                  {a.travelBatchMember?.travelBatch.departureDate && (
                    <Chip
                      size="small"
                      label={`Departure: ${new Date(a.travelBatchMember.travelBatch.departureDate).toLocaleDateString()}`}
                    />
                  )}
                </Stack>
              </Box>
              <Stack spacing={1} sx={{ width: 200 }}>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={a.status}
                  disabled={!canApprove}
                  helperText={!canApprove ? 'Requires approval permission' : undefined}
                  onChange={(e) =>
                    changeStatusMutation.mutate({ id: a.id, status: e.target.value as ApplicationStatus })
                  }
                >
                  {APPLICATION_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Visa status"
                  value={a.visaStatus?.id ?? ''}
                  onChange={(e) => setVisaStatus.mutate({ id: a.id, visaStatusId: Number(e.target.value) })}
                >
                  {visaStatusesQuery.data?.map((v) => (
                    <MenuItem key={v.id as number} value={v.id as number}>
                      {String(v.name)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  startIcon={<InfoOutlinedIcon />}
                  onClick={() => setDetailsApplicationId(a.id)}
                >
                  Details
                </Button>
                {canRequestTransfer && !NON_TRANSFERABLE_STATUSES.includes(a.status) && (
                  <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => setTransferApplication(a)}>
                    Transfer intake / batch
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        ))}
        {applicationsQuery.data?.data.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No applications yet.
          </Typography>
        )}
      </Stack>

      <StudentTransferRequests studentId={studentId} highlightId={highlightTransferId} />
      <RequestTransferDialog application={transferApplication} onClose={() => setTransferApplication(null)} />

      <Dialog open={qualOpen} onClose={() => setQualOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add qualification</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth value={qualName} onChange={(e) => setQualName(e.target.value)} />
            <TextField
              label="Institution"
              fullWidth
              value={qualInstitution}
              onChange={(e) => setQualInstitution(e.target.value)}
            />
            <TextField label="Year completed" type="number" fullWidth value={qualYear} onChange={(e) => setQualYear(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQualOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!qualName.trim() || !qualInstitution.trim() || addQualification.isPending}
            onClick={() => addQualification.mutate()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={appOpen} onClose={() => setAppOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New application</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Country"
              fullWidth
              value={countryId}
              onChange={(e) => {
                setCountryId(Number(e.target.value));
                setUniversityId('');
                setFacultyId('');
                setCourseId('');
              }}
            >
              {countriesQuery.data?.map((c) => (
                <MenuItem key={c.id as number} value={c.id as number}>
                  {String(c.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="University"
              fullWidth
              disabled={countryId === ''}
              value={universityId}
              onChange={(e) => {
                setUniversityId(Number(e.target.value));
                setFacultyId('');
                setCourseId('');
              }}
            >
              {universitiesQuery.data?.map((u) => (
                <MenuItem key={u.id as number} value={u.id as number}>
                  {String(u.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Faculty"
              fullWidth
              disabled={universityId === ''}
              value={facultyId}
              onChange={(e) => {
                setFacultyId(Number(e.target.value));
                setCourseId('');
              }}
            >
              {facultiesQuery.data?.map((f) => (
                <MenuItem key={f.id as number} value={f.id as number}>
                  {String(f.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Program"
              fullWidth
              disabled={facultyId === ''}
              value={courseId}
              onChange={(e) => setCourseId(Number(e.target.value))}
            >
              {coursesQuery.data?.map((c) => (
                <MenuItem key={c.id as number} value={c.id as number}>
                  {String(c.name)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!courseId || addApplication.isPending} onClick={() => addApplication.mutate()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <ApplicationDetailsDialog
        applicationId={detailsApplicationId}
        onClose={() => setDetailsApplicationId(null)}
      />
    </Box>
  );
};
